import { Button } from 'flowbite-react';
import { enqueueSnackbar } from 'notistack';
import { Dispute } from 'api/escrow/types';
import { useDataValues } from 'providers/DataValues';
import { useAcceptTriageResolutionMutation } from 'api/mutations/escrow/accept-triage-resolution';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { getDisputeStatusQueryKey } from 'api/queries/escrow/get-dispute-status';

type Props = { dispute: Dispute };

export default function TriageSection({ dispute }: Props) {
  const { userId } = useDataValues();
  const { mutateAsync: acceptTriage, isPending } = useAcceptTriageResolutionMutation();

  const isBuyer = dispute.tradeEscrow?.buyerId === +userId;
  const myAccepted = isBuyer ? dispute.triageAcceptedByBuyer : dispute.triageAcceptedBySeller;
  const counterpartyAccepted = isBuyer ? dispute.triageAcceptedBySeller : dispute.triageAcceptedByBuyer;

  const handleAccept = async () => {
    try {
      await acceptTriage({ disputeId: dispute.id });
      enqueueSnackbar('Resolution accepted', { variant: 'success' });
      void memoryQueryClient.refetchQueries({ queryKey: getDisputeStatusQueryKey(dispute.id) });
    } catch {
      // Error already handled by mutation's onError
    }
  };

  return (
    <div className="bg-brand-purple-card rounded-lg p-4 md:p-6">
      <h3 className="text-lg font-bold text-brand-black mb-4">Triage Resolution</h3>

      {dispute.triageProposedResolution ? (
        <>
          <div className="bg-white rounded-lg p-3 mb-4 border border-brand-purple-border">
            <p className="text-xs text-gray-500 mb-1">Proposed Resolution</p>
            <p className="text-sm text-brand-black whitespace-pre-wrap">{dispute.triageProposedResolution}</p>
          </div>

          <div className="flex flex-col gap-2 mb-4">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${myAccepted ? 'bg-green-500' : 'bg-gray-300'}`} />
              <span className="text-sm text-brand-black">You: {myAccepted ? 'Accepted' : 'Not yet accepted'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${counterpartyAccepted ? 'bg-green-500' : 'bg-gray-300'}`} />
              <span className="text-sm text-brand-black">
                Other party: {counterpartyAccepted ? 'Accepted' : 'Not yet accepted'}
              </span>
            </div>
          </div>

          {!myAccepted && dispute.status === 'open_triage' && (
            <Button
              pill
              color="failure"
              className="bg-brand-red-main font-semibold"
              onClick={handleAccept}
              isProcessing={isPending}
              disabled={isPending}
            >
              Accept Resolution
            </Button>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500">
          A support agent is reviewing your case. A resolution will be proposed here.
        </p>
      )}
    </div>
  );
}
